/**
 * نشانِ «از Pals & Plans آمد» روی لینک‌های خروجی (WHOSUP-167).
 *
 * وقتی کاربر از داخلِ محصول روی لینکی می‌زند که به سایتِ بیرونی می‌برد —
 * صفحه‌ی رسمیِ رخداد، خریدِ بلیط، لینکِ هر سانس — آن لینک `utm_source=outami.com`
 * می‌گیرد تا فردا بشود به برگزارکننده گفت «فلان‌قدر آدم از ما آمدند».
 *
 * ═══ چه لینکی نشان نمی‌گیرد ═══
 *
 *   • **سایتِ رقیب.** به رقیب دلیلی ندارد بگوییم کاربرش را ما فرستادیم.
 *     فهرستش در جدولِ `competitor_domains` است (مایگریشن 0134، سربرگِ
 *     «سایت‌های رقیب» در ادمین)؛ `competitorRepo.refresh()` موقعِ بوتِ هر دو
 *     اپ آن را این‌جا می‌نشاند و تا نرسیده، فهرستِ سفت‌شده‌ی پایین کار می‌کند.
 *   • **لینکی که خودش `utm_` دارد.** یعنی منبع کمپینِ خودش را داشته و
 *     بازنویسی داده‌ی او را خراب می‌کند.
 *   • **مسیریابی و لینک‌های دعوتِ خودمان.** اصلاً از این تابع رد نمی‌شوند —
 *     گوگل‌مپ با utm کاری نمی‌کند و `/i/`،`/s/` ورودی‌اند نه خروجی. لینک‌های
 *     اپِ ادمین هم عمداً خام می‌مانند: کلیکِ خودمان «ارجاع» نیست.
 *
 * URLِ خراب یا خالی دست‌نخورده برمی‌گردد، نه خطا — این لینک‌ها از کرالر
 * می‌آیند و همه‌شان تمیز نیستند (همان قاعده‌ی `domainOf` در lib/url.ts).
 */

/** پارامتری که به لینکِ خروجی می‌چسبد. */
export const UTM_PARAM = 'utm_source=outami.com';

/**
 * رقیب‌های شناخته‌شده — همان seedِ مایگریشن 0134، و فقط پشتوانه‌ی قبل از
 * رسیدنِ جدول: به‌محضِ جوابِ `competitorRepo.refresh()` هرچه دیتابیس بگوید
 * جایش را می‌گیرد. تغییرِ فهرستِ واقعی از ادمین است، نه این‌جا.
 */
export const DEFAULT_COMPETITOR_DOMAINS = [
  'eventbrite.com',
  'eventbrite.ca',
  'allevents.in',
  'meetup.com',
  'feverup.com',
  '10times.com',
];

/**
 * `https://www.Eventbrite.com:443/e/x` → `eventbrite.com`
 *
 * مثل `domainOf` ولی سخت‌گیرتر: پورت و کوئریِ بدونِ مسیر را هم می‌اندازد،
 * چون این‌جا خروجی کلیدِ مقایسه است نه متنِ نمایشی.
 */
const normalizeDomain = (raw: string): string => raw
  .trim().toLowerCase()
  .replace(/^https?:\/\//, '')
  .replace(/^www\./, '')
  .replace(/[/?#:].*$/, '');

let competitors: string[] = [...DEFAULT_COMPETITOR_DOMAINS];

/**
 * فهرستِ فعلی را عوض می‌کند — تنها صداکننده‌اش `competitorRepo.refresh()` است
 * (و تست‌ها). فهرستِ خالی یعنی واقعاً هیچ رقیبی نیست، نه «برگرد به پیش‌فرض»:
 * ادمینی که همه را پاک کرده همین را خواسته.
 */
export function setCompetitorDomains(domains: string[]): void {
  competitors = domains.map(normalizeDomain).filter(Boolean);
}

/** این لینک به یکی از سایت‌های رقیب می‌برد؟ زیردامنه هم می‌گیرد. */
export function isCompetitorUrl(url?: string | null): boolean {
  const host = normalizeDomain(url ?? '');
  if (!host) return false;
  return competitors.some((d) => host === d || host.endsWith(`.${d}`));
}

/** لینکِ خروجی، با نشان — یا همان لینک، اگر جزو استثناهای بالا باشد. */
export function withUtm(url?: string | null): string {
  const s = (url ?? '').trim();
  // فقط http(s). چیزِ دیگر (خالی، mailto:، متنِ خراب) همان‌طور برمی‌گردد.
  if (!/^https?:\/\//i.test(s)) return s;
  if (/[?&]utm_/i.test(s)) return s;
  if (isCompetitorUrl(s)) return s;

  // پارامتر باید قبل از `#` بنشیند، وگرنه بخشی از fragment می‌شود و به
  // سرور اصلاً نمی‌رسد.
  const hashAt = s.indexOf('#');
  const base = hashAt === -1 ? s : s.slice(0, hashAt);
  const fragment = hashAt === -1 ? '' : s.slice(hashAt);
  return `${base}${base.includes('?') ? '&' : '?'}${UTM_PARAM}${fragment}`;
}
